import { differenceInDays, startOfDay } from 'date-fns/esm'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { openUrl } from '@/utils/external-resources'
import { removeMarkdown } from '@/utils/markdown'
import { useNotificationCenter } from '../../hooks/useNotificationCenter'
import {
  StCard,
  StDate,
  StDescription,
  StDot,
  StDotContainer,
  StIcon,
  StIconContainer,
} from './NotificationCard.styled'

import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { NotificationMessage, NotificationType } from '../../types/notificiation-types'

type Props = {
  notification: NotificationMessage
  onClick?: () => void
}

const cardIcon: Record<NotificationType, IconProp> = {
  Message: ['fal', 'message'],
  Appointment: ['fal', 'calendar-day'],
  Intake: ['fal', 'house-medical'],
  Task: ['fal', 'clipboard-check'],
  Brochure: ['fal', 'book'],
  Invoice: ['fal', 'file-invoice'],
  Document: ['fal', 'file'],
  Action: ['fal', 'message-check'],
  PatientCommunication: ['fal', 'question-circle'],
}

const NotificationCard = ({ notification, onClick }: Props) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const icon = cardIcon[notification.type]

  const {
    setNotificationAsRead: { execute: setNotificationAsRead },
    setPatientCommunicationAsRead: { execute: setPatientCommunicationAsRead },
  } = useNotificationCenter()

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function handleClick() {
    if (notification.type === 'PatientCommunication') {
      setPatientCommunicationAsRead(notification.id)
    } else if (notification.type !== 'Action') {
      setNotificationAsRead(notification.id)
    }

    if (onClick) {
      onClick()
    }

    if (notification.attachment) {
      openUrl(notification.attachment)
    } else {
      navigate(`/timeline/activity/${notification.id}`)
    }
  }
  // Render
  // ------------------------------------------------------------------------- /

  const renderDate = () => {
    const difference = differenceInDays(startOfDay(new Date(notification.date)), new Date())
    if (difference < 0) {
      return formatLocale(notification.date, 'dd-MM-yyyy')
    } else if (difference === 0) {
      return formatLocale(notification.date, 'HH:mm')
    } else {
      return `${difference}d`
    }
  }

  return (
    <StCard gap={theme.UI.Spacing.Space4} alignItems='center' onClick={handleClick}>
      <StIconContainer type={notification.type}>
        {notification.read ? null : (
          <StDotContainer>
            <StDot color={theme.Components.Button.PrimaryWarning.Fill} />
          </StDotContainer>
        )}
        <StIcon icon={icon} />
      </StIconContainer>

      <FlexColumn gap={theme.UI.Spacing.Space1} flexGrow={1} style={{ minWidth: 0 }}>
        {notification.display ? (
          <H4 noMargin color={theme.Components.Card.Text} style={notification.read ? { fontWeight: 400 } : undefined}>
            {notification.display}
          </H4>
        ) : null}
        {notification.description ? (
          <StDescription type='BodyMediumRegular' style={{ fontWeight: 400 }} color={theme.Components.Card.Text}>
            {removeMarkdown(notification.description)}
          </StDescription>
        ) : null}
      </FlexColumn>

      <StDate type='BodyMediumRegular' color={theme.Colors.Neutrals.Dark}>
        {renderDate()}
      </StDate>
    </StCard>
  )
}

export { NotificationCard }
